import { useEffect, useMemo, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import { $createAdNode, AdNode } from '../../nodes/AdNode';
import { LexicalAdRulesEngine } from './LexicalAdRulesEngine';

type AdPlacementPluginProps = {
  articleId?: string;
};
const AdPlacementPlugin = ({ articleId }: AdPlacementPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const isProcessingRef = useRef(false);

  // Memoize the adRulesEngine to prevent re-creation on every render
  const adRulesEngine = useMemo(() => {
    return new LexicalAdRulesEngine({
      minAdsPerArticle: 1,
      maxAdsPerArticle: 3,
      minWordsBetweenAds: 500,
      minNodesBetweenAds: 4
    });
  }, []); // Empty dependency array ensures it is created only once.

  useEffect(() => {
    const handleContentUpdate = () => {
      // Prevent infinite loops by checking if we're already processing
      if (isProcessingRef.current) return;

      isProcessingRef.current = true;

      editor.update(() => {
        const root = editor.getEditorState().read(() => $getRoot());
        const nodes = root.getChildren();

        let wordsSinceLastAd = 0;
        let nodesSinceLastAd = 0;
        let lastAdNodeIndex = -1;
        let adCount = 0;

        // Calculate total word count and node count first
        let totalWordCount = 0;
        let totalNodeCount = 0;

        // First pass - count existing ads and calculate total words/nodes
        nodes.forEach((node) => {
          if (node instanceof AdNode) {
            adCount++;
          } else {
            const textContent = node.getTextContent();
            if (textContent) {
              totalWordCount += textContent.split(/\s+/).length;
              totalNodeCount++;
            }
          }
        });

        // Second pass - evaluate rules and place ads
        let adsInserted = 0;
        nodes.forEach((node, index) => {
          const textContent = node.getTextContent();
          if (node instanceof AdNode) {
            wordsSinceLastAd = 0;
            nodesSinceLastAd = 0;
            lastAdNodeIndex = index;
          } else if (textContent) {
            wordsSinceLastAd += textContent.split(/\s+/).length;
            nodesSinceLastAd++;
          }

          if (index > lastAdNodeIndex) {
            const context = {
              node,
              nextNode: nodes[index + 1] || null,
              wordsSinceLastAd,
              nodesSinceLastAd,
              totalWordCount,
              totalNodeCount,
              adCount: adCount + adsInserted,
              isLastNode: index === nodes.length - 1
            };

            const { shouldPlace, reason } = adRulesEngine.evaluateRules(context);

            if (shouldPlace) {
              const newAdNode = $createAdNode(
                process.env.REACT_APP_AD_SENSE_ID,
                '3976607715',
                articleId
              );
              node.insertAfter(newAdNode);
              wordsSinceLastAd = 0;
              nodesSinceLastAd = 0;
              lastAdNodeIndex = index + 1;
              adsInserted++;
            }
          }
        });
      });

      // Set a timeout to reset the processing flag to prevent immediate re-processing
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 100);
    };

    // Run once initially
    handleContentUpdate();

    // Use a debounced update listener to avoid rapid re-processing
    let timeoutId: NodeJS.Timeout | null = null;

    const removeListener = editor.registerUpdateListener(({ editorState, prevEditorState }) => {
      // Skip if we're in processing mode
      if (isProcessingRef.current) return;

      // Skip if it's the same state (might happen if our own updates trigger this)
      if (editorState === prevEditorState) return;

      // Debounce the update
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        handleContentUpdate();
      }, 1500);
    });

    return () => {
      // Clean up
      if (timeoutId) clearTimeout(timeoutId);
      removeListener();
    };
  }, [editor, adRulesEngine]);

  return null;
};

export default AdPlacementPlugin;
